/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ActionCloseArgs, type BaseRequestOptionsArgs, type HttpResponse, type Rest, BatchService, HttpService, Status } from '@dm/core';
import { type WindowsLocalUser } from 'app/common/types/mo';
import { type Observable } from 'rxjs';

import { AccountTypeE, WindowsLocalAccessUserStrService } from './windows-local-access-user-str.service';

@Injectable({ providedIn: 'root' })
export class WindowsLocalAccessUserService {
    constructor(
        private http: HttpService,
        private batchService: BatchService,
        private str: WindowsLocalAccessUserStrService,
    ) { }

    /**
     * 获取批量查询本地认证用户的rest
     */
    getBatchRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('windowsUser.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 查询单个本地认证用户
     * @param rid
     */
    get(rid: string, vstoreId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUser.get')
            .format({
                rid,
            })
            .setParams({
                vstoreId,
            });
        return this.http.get(rest);
    }

    /**
     * 创建本地认证用户
     * @param params
     * @param options
     */
    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUser.post');
        return this.http.post(rest, params, options);
    }

    /**
     * 修改本地认证用户
     * @param rid
     * @param params
     * @param options
     */
    modify(rid: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUser.put').format({ rid });
        return this.http.put(rest, params, options);
    }

    /**
     * 修改用户状态
     * @param dataList
     * @param status
     */
    modifyStatus(dataList: WindowsLocalUser[], status: boolean, callback: (args: ActionCloseArgs) => void): void {
        const cmds = dataList.map((data) => ({
            name: this.str.modifyStatusResult(data.NAME, status),
            action: this.modify(data.RID, { STATUSENABLE: status, vstoreId: data.vstoreId }),
        }));
        this.batchService.exePrompt(cmds, {
            onClose: (results) => {
                callback({ refresh: results.some((a) => a.status === Status.success) });
            },
        });
    }

    /**
     * 删除本地认证用户
     * @param rid
     * @param options
     */
    delete(rid: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUser.delete').format({ rid }).setParams({ vstoreId });
        return this.http.delete(rest, options);
    }

    /**
     * 获取所有多租户的Rest
     */
    getVstoreRest(): Rest {
        return this.http.makeUrl('vstore.getBatch');
    }

    /**
     * 获取查询本地用户组的rest
     */
    getGroupRest(vstoreId: string): Rest {
        return this.http.makeUrl('WindowsLocalGroup.getBatch').setParams({ vstoreId });
    }

    /**
     * 查询安全策略
     * @param options
     */
    getSecurityPolicy(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUserSecurityPolicy.get').setParams({ vstoreId });
        return this.http.get(rest, options);
    }

    /**
     * 设置安全策略
     * @param params
     * @param options
     */
    setSecurityPolicy(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('windowsUserSecurityPolicy.put');
        return this.http.put(rest, params, options);
    }

    /**
     * 添加用户到组
     * @param params
     */
    addToGroup(params: any): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroupMap.add');
        return this.http.post(rest, params);
    }

    /**
     * 用户组中移除用户
     */
    removeFromGroup(userName: string, groupName: string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('WindowsLocalGroupMap.remove')
            .setParams({
                NAME: userName,
                GROUPNAME: groupName,
                ACCOUNTTYPE: AccountTypeE.User,
                vstoreId,
            });

        return this.http.delete(rest, options);
    }
}
